// '/pure' does not inject js.stripe.com as an import side effect: the 253 KB script loads on the
// first loadStripe() call (checkout), not while every landing visitor boots (load-speed 2026-09-14).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeConstructorOptions } from '@stripe/stripe-js';
import pRetry, { AbortError } from 'p-retry';
import * as Sentry from '@sentry/vue';

/**
 * Stripe loader utility with retry logic using p-retry library
 * Addresses Sentry issue PCV4MAXPRO-1H: "Failed to load Stripe.js"
 */

/**
 * Load Stripe.js with retry logic and comprehensive error handling
 * Uses p-retry library for robust exponential backoff implementation
 */
export async function loadStripeWithRetry(
  publishableKey: string,
  options?: StripeConstructorOptions
): Promise<Stripe | null> {
  try {
    const stripe = await pRetry(
      async attemptNumber => {
        const startTime = performance.now();

        try {
          const stripeInstance = await loadStripe(publishableKey, options);

          const loadTime = performance.now() - startTime;

          // Log successful load with performance metrics
          Sentry.addBreadcrumb({
            category: 'stripe',
            message: 'Stripe.js loaded successfully',
            level: 'info',
            data: {
              attempt: attemptNumber,
              loadTime: `${loadTime.toFixed(2)}ms`,
              publishableKeyPrefix: publishableKey.substring(0, 12)
            }
          });

          return stripeInstance;
        } catch (error) {
          // Log failed attempt
          Sentry.addBreadcrumb({
            category: 'stripe',
            message: `Stripe.js load attempt ${attemptNumber} failed`,
            level: 'warning',
            data: {
              attempt: attemptNumber,
              error: error instanceof Error ? error.message : String(error)
            }
          });

          console.warn(`[Stripe Loader] Attempt ${attemptNumber} failed. Retrying...`, error);

          throw error;
        }
      },
      {
        retries: 3,
        factor: 2,
        minTimeout: 1000, // 1 second
        maxTimeout: 5000, // 5 seconds
        randomize: false,
        onFailedAttempt: error => {
          console.warn(
            `[Stripe Loader] Attempt ${error.attemptNumber} failed. ${error.retriesLeft} retries left.`
          );
        }
      }
    );

    return stripe;
  } catch (error) {
    // All attempts failed - log comprehensive error to Sentry
    const errorMessage = 'Failed to load Stripe.js after 3 attempts';

    Sentry.captureException(error, {
      tags: {
        component: 'stripe-loader',
        issue: 'PCV4MAXPRO-1H'
      },
      contexts: {
        stripe: {
          publishableKeyPrefix: publishableKey.substring(0, 12),
          attempts: 3,
          lastError: error instanceof Error ? error.message : 'Unknown error'
        },
        network: {
          online: navigator.onLine,
          effectiveType: (navigator as any).connection?.effectiveType || 'unknown',
          downlink: (navigator as any).connection?.downlink || 'unknown'
        }
      },
      level: 'error'
    });

    console.error(`[Stripe Loader] ${errorMessage}`, {
      publishableKey: `${publishableKey.substring(0, 12)}...`,
      attempts: 3,
      error
    });

    return null;
  }
}

/**
 * Singleton Stripe instance manager
 * Ensures only one Stripe instance is created and shared across the application
 */
class StripeInstanceManager {
  // eslint-disable-next-line no-use-before-define
  private static instance: StripeInstanceManager;

  private stripePromise: Promise<Stripe | null> | null = null;

  private stripe: Stripe | null = null;

  // eslint-disable-next-line no-useless-constructor, no-empty-function
  private constructor() {}

  public static getInstance(): StripeInstanceManager {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (!StripeInstanceManager.instance) {
      StripeInstanceManager.instance = new StripeInstanceManager();
    }
    return StripeInstanceManager.instance;
  }

  /**
   * Get or create Stripe instance
   */
  public async getStripe(
    publishableKey: string,
    options?: StripeConstructorOptions
  ): Promise<Stripe | null> {
    // Return cached instance if available
    if (this.stripe) {
      return this.stripe;
    }

    // Return in-flight promise if loading
    if (this.stripePromise) {
      return this.stripePromise;
    }

    // Create new loading promise
    this.stripePromise = loadStripeWithRetry(publishableKey, options);
    this.stripe = await this.stripePromise;

    return this.stripe;
  }

  /**
   * Get Stripe promise (for cases where promise is needed directly)
   */
  public getStripePromise(
    publishableKey: string,
    options?: StripeConstructorOptions
  ): Promise<Stripe | null> {
    if (this.stripePromise) {
      return this.stripePromise;
    }

    this.stripePromise = this.getStripe(publishableKey, options);
    return this.stripePromise;
  }

  /**
   * Reset instance (for testing or error recovery)
   */
  public reset(): void {
    this.stripe = null;
    this.stripePromise = null;
  }
}

// Export singleton instance
export const stripeManager = StripeInstanceManager.getInstance();

/**
 * Convenience function to get Stripe instance
 */
export async function getStripe(
  publishableKey: string,
  options?: StripeConstructorOptions
): Promise<Stripe | null> {
  return stripeManager.getStripe(publishableKey, options);
}

/**
 * Get Stripe promise (for Vuex store initialization)
 */
export function getStripePromise(
  publishableKey: string,
  options?: StripeConstructorOptions
): Promise<Stripe | null> {
  return stripeManager.getStripePromise(publishableKey, options);
}
